// Recherche fuzzy permissive pour les filtres de tables.
//
// La requête est découpée en termes (séparés par des espaces). Chaque terme
// doit apparaître dans le texte cible comme une sous-séquence (les caractères
// du terme dans l'ordre, pas forcément contigus), insensible à la casse, et
// les termes eux-mêmes dans l'ordre.
//
// Exemple : fuzzyMatch("srv db", "server-prod-database.local") === true
//   - "srv" : s…e…r…v…(er) → sous-séquence trouvée
//   - "db"  : (data)…b…(ase) → sous-séquence trouvée après "srv"

/** Vrai si `term` apparaît comme sous-séquence de `text` à partir de `from`.
 *  Retourne l'index juste après le dernier caractère consommé, ou -1. */
function subsequenceFrom(term: string, text: string, from: number): number {
  let ti = from;
  for (let qi = 0; qi < term.length; qi++) {
    const ch = term[qi];
    let found = -1;
    for (let i = ti; i < text.length; i++) {
      if (text[i] === ch) {
        found = i;
        break;
      }
    }
    if (found === -1) return -1;
    ti = found + 1;
  }
  return ti;
}

/** Vrai si chaque terme de `query` est une sous-séquence de `text`, dans l'ordre.
 *  Une requête vide matche tout. Insensible à la casse. */
export function fuzzyMatch(query: string, text: string): boolean {
  const q = (query ?? "").trim().toLowerCase();
  if (!q) return true;
  const haystack = (text ?? "").toLowerCase();
  const terms = q.split(/\s+/).filter(Boolean);
  let cursor = 0;
  for (const term of terms) {
    const next = subsequenceFrom(term, haystack, cursor);
    if (next === -1) return false;
    cursor = next;
  }
  return true;
}

/** Filtre une liste : conserve les items dont le texte (via `getText`) matche
 *  la requête fuzzy. Une requête vide renvoie la liste inchangée. */
export function fuzzyFilter<T>(query: string, items: readonly T[], getText: (item: T) => string): T[] {
  const q = (query ?? "").trim();
  if (!q) return items.slice();
  return items.filter((item) => fuzzyMatch(q, getText(item)));
}
